import { styled } from '@mui/material';
import { HeaderCoverResizeType } from '../../models/enum';
import { ContentWrapper } from '../../styles/shared';
import { getCompany } from '../../utils/company';
import { Companies } from '../../models/domain/enum';

const company = getCompany();

const getMarkdownWrapperStyles = (theme: any) => {
  switch (company) {
    case Companies.SIRMAN:
      return {
        lineHeight: theme.spacings.unit(27 / 4),
        '> div': {
          display: 'flex',
          maxWidth: theme.spacings.unit(626 / 4),
          flexDirection: 'column',
          [theme.breakpointsMediaQuery.down('sTextNews')]: {
            maxWidth: '100%',
          },
        },
        '> div:first-of-type': {
          padding: 0,
        },
        p: {
          margin: `${theme.fonts.utility.size(8)} 0 0 0`,
        },
      };
    case Companies.FELSINEA:
      return {
        lineHeight: theme.spacings.unit(28.8 / 4),
        marginBottom: theme.spacings.unit(24),
        '> div': {
          display: 'flex',
          maxWidth: '55%',
          flexDirection: 'column',
          [theme.breakpointsMediaQuery.down('sTextNews')]: {
            maxWidth: '100%',
          },
        },
        '> div:first-of-type': {
          padding: 0,
        },
        p: {
          margin: `${theme.fonts.utility.size(8)} 0 0 0`,
        },
      };
  }
};

export const StyledContentWrapper = styled(ContentWrapper)(() => ({
  padding: 0,
}));

export const OverlayStyled = styled('div', {
  shouldForwardProp: (prop) => !['overlay'].includes(prop as string),
})(({ overlay }) => ({
  backgroundColor: `${overlay ? overlay + '80' : 'transparent'}`,
  gridArea: '1 / 1 / 1 / 1',
  position: 'relative',
}));

export const ImageContainerStyled = styled('div', {
  shouldForwardProp: (prop) => !['headerCoverResize'].includes(prop as string),
})(() => ({
  gridArea: '1 / 1 / 1 / 1',
  '> span': {
    height: '100% !important',
  },
}));

export const StyledVideo = styled('video')(({ objectFit }) => ({
  width: '100%',
  height: '100%',
  top: '50%',
  transform: 'translateY(-50%)',
  position: 'relative',
  objectFit: objectFit || 'cover',
}));

export const ImageContainerDefaultStyled = styled('div', {
  shouldForwardProp: (prop) => !['headerCoverResize'].includes(prop as string),
})(({ theme, headerCoverResize }) => ({
  position: 'relative',
  display: 'block',
  [theme.breakpointsMediaQuery.down('sm')]: {
    minHeight: (() => {
      switch (headerCoverResize) {
        default:
        case HeaderCoverResizeType.DEFAULT_COVER:
          return '30vh';
        case HeaderCoverResizeType.CONTAIN:
          return 'auto';
        case HeaderCoverResizeType.HIDDEN:
          return 0;
      }
    })(),
  },
  [theme.breakpointsMediaQuery.up('sm')]: {
    height: '100%',
    maxHeight: 'none',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    zIndex: -3,
  },
}));

export const CoverContainer = styled('div', {
  shouldForwardProp: (prop) => !['headerCoverResize'].includes(prop as string),
})(({ theme, headerCoverResize }) => ({
  position: 'relative',
  display: 'grid',
  width: '100%',
  color: theme.colors.primaryText,
  '&:after': {
    opacity: 1,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    zIndex: -1,
  },
  [theme.breakpointsMediaQuery.up('sm')]: {
    minHeight: (() => {
      switch (headerCoverResize) {
        default:
        case HeaderCoverResizeType.DEFAULT_COVER:
          return theme.spacings.unit(125);
        case HeaderCoverResizeType.CONTAIN:
          return 'auto';
        case HeaderCoverResizeType.HIDDEN:
          return 0;
      }
    })(),
    color: theme.colors.white,
  },
  [theme.breakpointsMediaQuery.up('md')]: {
    height: (() => {
      switch (headerCoverResize) {
        default:
        case HeaderCoverResizeType.DEFAULT_COVER:
          return theme.spacings.unit(150);
        case HeaderCoverResizeType.CONTAIN:
          return 'auto';
        case HeaderCoverResizeType.HIDDEN:
          return 0;
      }
    })(),
  },
  [theme.breakpointsMediaQuery.up('xl')]: {
    height: (() => {
      switch (headerCoverResize) {
        default:
        case HeaderCoverResizeType.DEFAULT_COVER:
          return theme.spacings.unit(187.5);
        case HeaderCoverResizeType.CONTAIN:
          return 'auto';
        case HeaderCoverResizeType.HIDDEN:
          return 0;
      }
    })(),
  },
}));

export const Category = styled('p', {
  shouldForwardProp: (prop) =>
    ![
      'fontSize',
      'fontMobileSize',
      'fontMobileWeight',
      'fontMobileWeight',
    ].includes(prop as string),
})(({ theme }) => ({
  textTransform: 'uppercase',
  margin: 0,
  marginTop: theme.spacings.unit(3),
  fontSize: theme.fonts.utility.size(7),
  fontWeight: 'bold',
  color: theme.colors.secondary,
}));

export const InformationRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacings.unit(6),
  lineHeight: theme.spacings.unit(12),
}));

export const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: '6px',
  backgroundColor: theme.colors.borderSecondary,
  justifyContent: 'center',
  height: theme.spacings.unit(12),
  width: theme.spacings.unit(12),
  alignItems: 'center',
}));

export const LinkStyled = styled('a')(({ theme }) => ({
  fontSize: theme.fonts.utility.size(10),
  letterSpacing: theme.spacings.unit(0.125),
  color: theme.colors.ninthColor,
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: `calc(100% - ${theme.spacings.unit(18)})`,
}));

export const LabelStyled = styled('h2')(({ theme }) => ({
  margin: 0,
  fontSize: theme.fonts.utility.size(10),
  fontWeight: 'inherit',
  alignSelf: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: `calc(100% - ${theme.spacings.unit(18)})`,
}));

export const InformationsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacings.unit(10),
}));

export const InformationRowsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacings.unit(6),
  padding: `${theme.spacings.unit(10)} 0`,
}));

export const Title = styled('h1', {
  shouldForwardProp: (prop) =>
    ![
      'fontSize',
      'fontMobileSize',
      'fontMobileWeight',
      'fontMobileWeight',
    ].includes(prop as string),
})(({ theme, fontSize, fontMobileSize, fontMobileWeight, fontWeight }) => ({
  margin: 0,
  marginTop: theme.fonts.utility.size(6),
  wordBreak: 'break-word',

  [theme.breakpointsMediaQuery.up('sm')]: {
    fontSize: fontSize
      ? `${fontSize * 0.5}px`
      : theme.fonts.utility.size((40 * 0.5) / 2),
    lineHeight: fontSize
      ? `${(fontSize + 6 * 0.5) * 0.5}px`
      : theme.fonts.utility.size((46 * 0.5) / 2),
    fontWeight: fontWeight ? (fontWeight as number) : 700,
  },

  [theme.breakpointsMediaQuery.up('md')]: {
    fontSize: fontSize
      ? `${fontSize * 0.6}px`
      : theme.fonts.utility.size((40 * 0.6) / 2),
    lineHeight: fontSize
      ? `${(fontSize + 6 * 0.6) * 0.6}px`
      : theme.fonts.utility.size((46 * 0.6) / 2),
  },

  [theme.breakpointsMediaQuery.up('lg')]: {
    fontSize: fontSize
      ? `${fontSize * 0.8}px`
      : theme.fonts.utility.size((40 * 0.8) / 2),
    lineHeight: fontSize
      ? `${(fontSize + 6 * 0.8) * 0.8}px`
      : theme.fonts.utility.size((46 * 0.8) / 2),
  },

  [theme.breakpointsMediaQuery.up('xl')]: {
    fontSize: fontSize ? `${fontSize}px` : theme.fonts.utility.size(40 / 2),
    lineHeight: fontSize
      ? `${fontSize + 6}px`
      : theme.fonts.utility.size(46 / 2),
  },

  [theme.breakpointsMediaQuery.down('sm')]: {
    fontSize: fontMobileSize
      ? `${fontMobileSize}px`
      : theme.fonts.utility.size(30 / 2),
    lineHeight: fontMobileSize
      ? `${fontMobileSize + 6}px`
      : theme.fonts.utility.size(34 / 2),
    fontWeight: fontMobileWeight ? fontMobileWeight : 700,
  },
}));

export const ContainerSymbols = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacings.unit(6),
  marginTop: theme.spacings.unit(6),
}));

export const ImageRowContainer = styled('div')(({ theme }) => ({
  width: theme.spacings.unit(6),
  height: theme.spacings.unit(6),
  position: 'relative',
  alignSelf: 'center',
}));

export const ContainerSingleSymbol = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '& > span': {
    color: theme.colors.primary,
    fontSize: theme.fonts.utility.size(6),
    marginBottom: theme.spacings.unit(1),
    fontWeight: '600',
  },
}));

export const MarkdownWrapper = styled('div', {
  shouldForwardProp: (prop) => !['lightContrast'].includes(prop as string),
})(({ theme, lightContrast }) => ({
  fontSize: theme.fonts.utility.size(9),
  display: 'flex',
  justifyContent: 'center',
  color: lightContrast ? theme.colors.white : theme.colors.primaryText,
  fontWeight: 400,
  ...getMarkdownWrapperStyles(theme),
}));

export const SectionHeaderContainer = styled('div')(({ theme }) => ({
  padding: `${theme.spacings.unit(12)} 0`,
  '& > div': {
    maxWidth: '55%',
    [theme.breakpointsMediaQuery.down('sTextNews')]: {
      maxWidth: '100%',
      padding: `0 calc(${theme.spacings.unit(
        9
      )} + env(safe-area-inset-right)) 0 calc(${theme.spacings.unit(
        9
      )} + env(safe-area-inset-left))`,
    },
  },
}));

export const MainInfoWrapper = styled('div', {
  shouldForwardProp: (prop) => !['lightContrast'].includes(prop as string),
})(({ theme, lightContrast }) => ({
  display: 'flex',
  justifyContent: 'center',
  color: lightContrast ? theme.colors.white : theme.colors.primaryText,
  width: '100%',
}));

export const MainInfoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: theme.spacings.unit(626 / 4),
  [theme.breakpointsMediaQuery.down('sTextNews')]: {
    maxWidth: '100%',
    padding: `0 calc(${theme.spacings.unit(
      36 / 4
    )} + env(safe-area-inset-right)) 0 calc(${theme.spacings.unit(
      36 / 4
    )} + env(safe-area-inset-left))`,
  },
}));
